import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CohortFilterGranularity } from '@/api/generated/model';
import { Input } from '@/comps/primitives/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/comps/primitives/select';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS } from '@/comps/FilterPicker/filter-content-v2/constants';
import { UserMetricSpecialFilterState } from '@/comps/FilterPicker/filter-content-v2/userMetricSpecialFilterAdapter';
import { FILTERS } from '@/models/common/filters';
import { WINDOW_GRANULARITY_OPTIONS } from '@/domains/live-activity/cohorts/components/rule-builder/cohort-filter-controls';
import { cn } from '@/utils/shad';
const POPOVER_CONTENT_CLASS = cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, 'border-zinc-800 bg-[#09090B]');
type Props = {
  initialState: UserMetricSpecialFilterState;
  onChange: (state: UserMetricSpecialFilterState) => void;
  onApply: (state: UserMetricSpecialFilterState) => void;
};
function parseNumberInput(event: ChangeEvent<HTMLInputElement>, integersOnly = false): number {
  const parsed = parseInt(event.target.value, 10);
  if (Number.isNaN(parsed)) return 0;
  return integersOnly ? Math.max(0, Math.round(parsed)) : parsed;
}
export function FilterContentV2UserMetricSpecialValuePopover(t0) {
  const $ = _c(64);
  const {
    initialState,
    onChange,
    onApply
  } = t0;
  let t1;
  if ($[0] !== initialState) {
    t1 = () => ({
      ...initialState
    });
    $[0] = initialState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, setState] = useState(t1);
  const onChangeRef = useRef(onChange);
  let t2;
  let t3;
  if ($[2] !== onChange) {
    t2 = () => {
      onChangeRef.current = onChange;
    };
    t3 = [onChange];
    $[2] = onChange;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== state) {
    t4 = () => {
      onChangeRef.current(state);
    };
    t5 = [state];
    $[5] = state;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = patch => {
      setState(current => ({
        ...current,
        ...patch
      }) as UserMetricSpecialFilterState);
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const updateState = t6;
  let t7;
  if ($[9] !== onApply || $[10] !== state) {
    t7 = () => {
      onApply(state);
    };
    $[9] = onApply;
    $[10] = state;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleApply = t7;
  if (state.column === FILTERS.activePeriods) {
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = cn(POPOVER_CONTENT_CLASS, "flex flex-col gap-3 p-3");
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = event => updateState({
        firstNumber: parseNumberInput(event, true)
      });
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== state.firstNumber) {
      t10 = <Input type="number" className="h-8 w-16 border-zinc-700 bg-transparent" value={state.firstNumber} onChange={t9} />;
      $[14] = state.firstNumber;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] !== state.comparatorKind || $[17] !== state.secondNumber) {
      t11 = state.comparatorKind === "between" ? <><span className="text-muted-foreground">and</span><Input type="number" className="h-8 w-16 border-zinc-700 bg-transparent" value={state.secondNumber} onChange={event_0 => updateState({
          secondNumber: parseNumberInput(event_0, true)
        })} /></> : null;
      $[16] = state.comparatorKind;
      $[17] = state.secondNumber;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    let t12;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <span className="text-muted-foreground">of the last</span>;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = event_1 => updateState({
        window: parseNumberInput(event_1, true)
      });
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    let t14;
    if ($[21] !== state.window) {
      t14 = <Input type="number" className="h-8 w-16 border-zinc-700 bg-transparent" value={state.window} onChange={t13} />;
      $[21] = state.window;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = granularity => updateState({
        granularity: granularity as CohortFilterGranularity
      });
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    let t16;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <SelectTrigger className="h-8 w-24 border-zinc-700 bg-transparent"><SelectValue /></SelectTrigger>;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    let t17;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <SelectContent className="z-1000!">{WINDOW_GRANULARITY_OPTIONS.map(_temp)}</SelectContent>;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    let t18;
    if ($[26] !== state.granularity) {
      t18 = <Select value={state.granularity} onValueChange={t15}>{t16}{t17}</Select>;
      $[26] = state.granularity;
      $[27] = t18;
    } else {
      t18 = $[27];
    }
    let t19;
    if ($[28] !== t10 || $[29] !== t11 || $[30] !== t14 || $[31] !== t18) {
      t19 = <div className="flex flex-wrap items-center gap-2 text-xs text-foreground">{t10}{t11}{t12}{t14}{t18}</div>;
      $[28] = t10;
      $[29] = t11;
      $[30] = t14;
      $[31] = t18;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] !== handleApply) {
      t20 = <button type="button" className="self-end rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground" onClick={handleApply}>Apply</button>;
      $[33] = handleApply;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    let t21;
    if ($[35] !== t19 || $[36] !== t20) {
      t21 = <div className={t8}>{t19}{t20}</div>;
      $[35] = t19;
      $[36] = t20;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    return t21;
  }
  let t8;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn(POPOVER_CONTENT_CLASS, "flex flex-col gap-3 p-3");
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event_2 => updateState({
      firstNumber: parseNumberInput(event_2, true)
    });
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== state.firstNumber) {
    t10 = <Input type="number" className="h-8 w-20 border-zinc-700 bg-transparent" value={state.firstNumber} onChange={t9} />;
    $[40] = state.firstNumber;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== state.comparatorKind || $[43] !== state.secondNumber) {
    t11 = state.comparatorKind === "between" ? <><span className="text-muted-foreground">and</span><Input type="number" className="h-8 w-20 border-zinc-700 bg-transparent" value={state.secondNumber} onChange={event_3 => updateState({
        secondNumber: parseNumberInput(event_3, true)
      })} /></> : null;
    $[42] = state.comparatorKind;
    $[43] = state.secondNumber;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-muted-foreground">seconds in the last</span>;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event_4 => updateState({
      windowValue: parseNumberInput(event_4, true)
    });
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== state.windowValue) {
    t14 = <Input type="number" className="h-8 w-16 border-zinc-700 bg-transparent" value={state.windowValue} onChange={t13} />;
    $[47] = state.windowValue;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = granularity_0 => updateState({
      windowGranularity: granularity_0 as CohortFilterGranularity
    });
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SelectTrigger className="h-8 w-24 border-zinc-700 bg-transparent"><SelectValue /></SelectTrigger>;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SelectContent className="z-1000!">{WINDOW_GRANULARITY_OPTIONS.map(_temp2)}</SelectContent>;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== state.windowGranularity) {
    t18 = <Select value={state.windowGranularity} onValueChange={t15}>{t16}{t17}</Select>;
    $[52] = state.windowGranularity;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t10 || $[55] !== t11 || $[56] !== t14 || $[57] !== t18) {
    t19 = <div className="flex flex-wrap items-center gap-2 text-xs text-foreground">{t10}{t11}{t12}{t14}{t18}</div>;
    $[54] = t10;
    $[55] = t11;
    $[56] = t14;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== handleApply) {
    t20 = <button type="button" className="self-end rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground" onClick={handleApply}>Apply</button>;
    $[59] = handleApply;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t19 || $[62] !== t20) {
    t21 = <div className={t8}>{t19}{t20}</div>;
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  return t21;
}
function _temp2(option_0) {
  return <SelectItem key={option_0.value} value={option_0.value}>{option_0.label}</SelectItem>;
}
function _temp(option) {
  return <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>;
}